import React, { createContext, useContext, useEffect, useState } from 'react';
import { loadFirestore } from '../lazyFirebase';

// SPEED: the site config comes via the Firestore REST API (a small fetch) — no need for the 250KB Firebase SDK.
const FS_PROJECT = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'organicslimfit-e7af4';
const FS_CONFIG_URL = `https://firestore.googleapis.com/v1/projects/${FS_PROJECT}/databases/(default)/documents/config/site_config`;
const parseFsValue = (v) => {
  if (!v) return undefined;
  if ('stringValue' in v) return v.stringValue;
  if ('integerValue' in v) return Number(v.integerValue);
  if ('doubleValue' in v) return v.doubleValue;
  if ('booleanValue' in v) return v.booleanValue;
  if ('nullValue' in v) return null;
  if ('timestampValue' in v) return v.timestampValue;
  if ('arrayValue' in v) return (v.arrayValue.values || []).map(parseFsValue);
  if ('mapValue' in v) return parseFsFields(v.mapValue.fields || {});
  return undefined;
};
const parseFsFields = (fields) => {
  const out = {};
  Object.keys(fields || {}).forEach(k => { const val = parseFsValue(fields[k]); if (val !== undefined) out[k] = val; });
  return out;
};

// Default values — mirror what the admin panel (public/osf-manage-panel.html)
// writes to Firestore doc "config/site_config". Anything the admin hasn't
// customized yet simply falls back to these.
export const DEFAULT_SITE_CONFIG = {
  slider_1: 'LIMITED STOCK - ORDER NOW',
  slider_2: 'WINTER SALE IS LIVE NOW',
  announce_1: '🌿 100% Natural Formula · Organic & Pure · Nationwide Delivery',
  announce_2: '🔥 Limited Stock! · Up to 30% OFF · Order Now!',

  pack1_name: '30 Days — Lose upto 6 Kg',
  pack1_sub: '1 Pack • 130G • 65–70 Cups',
  pack1_price: 1199,
  pack1_original: 1700,
  pack1_badge_label: 'STANDARD PACKAGE',

  pack2_name: '60 Days — Lose upto 12 Kg',
  pack2_sub: '2 Packs • 260G • 130–140 Cups',
  pack2_price: 2399,
  pack2_original: 3400,
  pack2_badge_label: 'PREMIUM PACKAGE',
  pack2_save_badge: 'Save More Rs.200',

  pack3_name: '90 Days — Lose upto 18 Kg',
  pack3_sub: '3 Packs • 390G • 200–210 Cups',
  pack3_price: 3199,
  pack3_original: 5100,
  pack3_badge_label: 'PRESIDENT PACKAGE',
  pack3_save_badge: 'Save More Rs.600',

  delivery: 199,
  product_title: 'Organic Slim Fit Tea',
  product_subtitle: '130G | 65–70 Cups Per Pack',
  product_image: '/images/product.webp',
  review_count: '50+',

  feature_1: '🌿 100% Herbal Natural Ingredients',
  feature_2: '🚚 Fast Delivery — Rs.199 Nationwide',
  feature_3: '🔒 Protected Body Wellness',

  hero_headline: 'ELEVATE YOUR WELLNESS. SLIM NATURALLY!',

  about_p1: 'Organic Slim Fit Tea is a carefully formulated herbal blend designed to support natural fat burn, gentle detoxification, and metabolic balance for modern lifestyles.',
  about_p2: 'Unlike conventional weight-loss teas made with harsh, low-quality ingredients, our formula offers a smooth, refreshing taste enjoyed daily without bitterness.',
  about_p3: 'Our "8-Ingredient Super Formula" features high-grade herbs — including Genuine Ceylon Cinnamon, Premium Cardamom, and Nigella Seeds.',
  about_p4: "With Organic Slim Fit, you're choosing a clean, effective, and premium solution for a healthier, slimmer lifestyle.",

  ing1_name: 'Unoxidized Tea', ing1_desc: 'Boosts metabolism and helps burn full-body fat naturally.', ing1_icon: '🍵',
  ing2_name: 'Fennel Seeds', ing2_desc: 'Clears digestion and reduces belly bloating for a flatter stomach.', ing2_icon: '🌿',
  ing3_name: 'Carom Seeds', ing3_desc: 'Targets stubborn belly fat and speeds up your weight loss.', ing3_icon: '🫚',
  ing4_name: 'Dried Mint', ing4_desc: 'Cools the body, stops extra hunger, and naturally detoxifies.', ing4_icon: '🌱',
  ing5_name: 'Ceylon Cinnamon', ing5_desc: 'Controls blood sugar to stop daily sweet cravings.', ing5_icon: '🟫',
  ing6_name: 'Nigella Seeds', ing6_desc: 'Builds strong daily immunity and improves overall health.', ing6_icon: '🌑',
  ing7_name: 'Cardamom', ing7_desc: 'Increases energy levels and speeds up the fat-burning process.', ing7_icon: '💚',
  ing8_name: 'Black Pepper', ing8_desc: 'Helps your body absorb all herbs quickly for faster results.', ing8_icon: '⚫',

  ingredients_title: '8 Premium Natural Ingredients',
  footer_cta_title: 'Ready to Start Your Journey? 🌿',
  footer_cta_sub: 'Order today — Cash on Delivery available across all of Pakistan',
  happy_customers_count: '1000+',

  banner_1: '', banner_2: '', banner_3: '', banner_4: '', banner_5: '', banner_6: '', banner_7: '',

  // Product gallery (multiple product photos) — set from Admin Panel → Product Images.
  // Until an image is uploaded from the admin panel, the website keeps showing its default product photos.
  product_img_1: '', product_img_2: '', product_img_3: '', product_img_4: '',
  product_img_5: '', product_img_6: '', product_img_7: '', product_img_8: '',

  // The video / GIF playing under the "Elevate Your Wellness" section — set from Admin Panel → Elevate Video.
  about_media_url: '', about_media_type: '', about_media_ratio: '', about_media_poster: '',
};

const SiteContentContext = createContext({ config: DEFAULT_SITE_CONFIG, loading: true });

// SPEED: site config (jis mein product images ke URLs hain) localStorage mein cache.
// A returning visitor does not wait for Firestore — the product image URL
// is known at first paint, so the image starts downloading immediately.
// The cache updates as soon as the fresh copy arrives from Firestore (stale-while-revalidate).
const CONFIG_CACHE_KEY = 'osf_site_config_v1';
const loadCachedConfig = () => {
  try {
    const raw = localStorage.getItem(CONFIG_CACHE_KEY);
    if (raw) return { ...DEFAULT_SITE_CONFIG, ...JSON.parse(raw) };
  } catch (e) { /* storage unavailable — defaults use honge */ }
  return null;
};

export function SiteContentProvider({ children }) {
  const [cached] = useState(loadCachedConfig); // only once, on first render
  const [config, setConfig] = useState(cached || DEFAULT_SITE_CONFIG);
  const [loading, setLoading] = useState(!cached);

  useEffect(() => {
    let unsub = () => {};
    let cancelled = false;
    const apply = (data) => {
      setConfig({ ...DEFAULT_SITE_CONFIG, ...data });
      setLoading(false);
      try { localStorage.setItem(CONFIG_CACHE_KEY, JSON.stringify(data)); } catch (e) { /* ignore */ }
    };
    const start = async () => {
      if (cancelled) return;
      try {
        const res = await fetch(FS_CONFIG_URL);
        if (res.status === 404) { if (!cancelled) apply({}); return; }
        if (!res.ok) throw new Error('REST ' + res.status);
        const json = await res.json();
        if (!cancelled) apply(parseFsFields(json.fields));
      } catch (restErr) {
        // Fallback: agar REST kisi wajah se fail ho to purana SDK tareeqa (dynamic load)
        try {
          const { db, doc, onSnapshot } = await loadFirestore();
          if (cancelled) return;
          unsub = onSnapshot(doc(db, 'config', 'site_config'),
            (snap) => apply(snap.exists() ? snap.data() : {}),
            (err) => { console.warn('Config load error:', err.message); setLoading(false); });
        } catch (e) { setLoading(false); }
      }
    };
    // If a cache exists, open the Firestore connection after first paint (during idle time) — keep the main thread free.
    // Cache na ho (pehli visit) to foran.
    let idleId, timerId;
    if (cached) {
      if (window.requestIdleCallback) idleId = window.requestIdleCallback(start, { timeout: 2500 });
      else timerId = setTimeout(start, 1200);
    } else {
      start();
    }
    return () => {
      cancelled = true;
      if (idleId && window.cancelIdleCallback) window.cancelIdleCallback(idleId);
      if (timerId) clearTimeout(timerId);
      unsub();
    };
  }, []);

  return (
    <SiteContentContext.Provider value={{ config, loading }}>
      {children}
    </SiteContentContext.Provider>
  );
}

export const useSiteContent = () => useContext(SiteContentContext);
